import {
  AccessTime,
  BusinessCenter,
  ExpandMore,
  Filter,
  PersonSearch,
  Room,
  Settings
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import React, { useState } from 'react';
import AttachmentBox from '../../components/AttachmentBox/AttachmentBox';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import TypographyDash from '../../components/TypographyDash/TypographyDash';
import { ProjectPaymentType } from '../../enums/projectPaymentType';
import { ProjectStatus } from '../../enums/projectStatus';
import { Project } from '../../models/project';
import { fileService } from '../../services/fileService';
import { getProjectStatusStyle } from '../../utils/projectStatus';
import MessageModal from './MessageModal';

type ProjectCardProps = {
  project: Project;
  onAcceptProject: (event: React.MouseEvent<unknown>, project: Project) => void;
  onDeclineProject: (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => void;
};

const ProjectCard = ({
  project,
  onAcceptProject,
  onDeclineProject
}: ProjectCardProps): JSX.Element => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const projectFieldsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    color: '#888888',
    marginBottom: '8px'
  };

  const expandedOptions = [
    {
      title: 'Company:',
      value: project.companyName ?? '--',
      icon: <BusinessCenter />
    },
    {
      title: 'Project created by:',
      value: project.projectOwner ?? '--',
      icon: <PersonSearch />
    },
    {
      title: 'Date/Time:',
      value:
        project.startDate && project.endDate
          ? `${format(new Date(project.startDate), 'MMM do')} - ${format(
              new Date(project.endDate),
              'MMM do'
            )}`
          : '--',
      icon: <AccessTime />
    },
    {
      title: 'Location:',
      value: project.address ?? '--',
      icon: <Room />
    },
    {
      title: 'Category:',
      value:
        project.category == '' || !project.category ? '--' : project.category,
      icon: <Filter />
    },
    {
      title: 'Expanse code:',
      value:
        project.expenseCode == '' || !project.expenseCode
          ? '--'
          : project.expenseCode,
      icon: <Settings />,
      overrideStyle: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        color: '#888888'
      }
    }
  ];

  const closedOptions = [
    {
      title: 'Company:',
      value: project.companyName ?? '--',
      icon: <BusinessCenter />
    },
    {
      title: 'Date/Time:',
      value: project.endDate
        ? format(new Date(project.endDate), 'MMM do, Y')
        : '--',
      icon: <AccessTime />
    },
    {
      title: 'Location:',
      value: project.address ?? '--',
      icon: <Room />,
      overrideStyle: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        color: '#888888'
      }
    }
  ];

  const sectionDetailsStyle = {
    borderTop: '0.75px #DDDDDD solid',
    marginTop: '16px',
    paddingTop: '16px'
  };

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(uuid).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  return (
    <Accordion
      onChange={() => {
        setExpanded((ex) => !ex);
      }}
      key={project.id}
      sx={{ padding: '12px 0px', marginBottom: '18px' }}
    >
      <AccordionSummary
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{ marginLeft: '16px', marginRight: '16px' }}
      >
        <Grid container spacing={2}>
          <Grid item xs={3} sx={{ position: 'relative' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                width: '100%',
                paddingRight: '15px'
              }}
            >
              <ExpandMore
                sx={expanded ? {} : { transform: 'rotate(180deg)' }}
              />
              <Typography
                variant="h5"
                sx={{ marginLeft: '10px' }}
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
              >
                {project.projectTitle}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            {expanded && (
              <Typography variant="subtitle2">
                {expandedOptions.map((option) => (
                  <Box
                    sx={option.overrideStyle ?? projectFieldsStyle}
                    key={option.title}
                  >
                    {option.icon}
                    <Typography variant="body2" color={'#888888'}>
                      {option.title}
                    </Typography>
                    <Typography variant="body2">{option.value}</Typography>
                  </Box>
                ))}
              </Typography>
            )}
            {!expanded && (
              <Typography variant="subtitle2">
                {closedOptions.map((option) => (
                  <Box
                    sx={option.overrideStyle ?? projectFieldsStyle}
                    key={option.title}
                  >
                    {option.icon}
                    <Typography variant="body2" color={'#888888'}>
                      {option.value}
                    </Typography>
                  </Box>
                ))}
              </Typography>
            )}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4">
              {project.budget ? (
                <FormatCurrency value={project.budget} />
              ) : (
                '--'
              )}
            </Typography>
            <Typography variant="body2" color={'#888888'}>
              {ProjectPaymentType[project.paymentType]}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Box display="flex" justifyContent="flex-end">
              <Box
                sx={{
                  ...getProjectStatusStyle(ProjectStatus[project.status]),
                  borderRadius: '20px',
                  padding: '8px 16px'
                }}
              >
                {ProjectStatus[project.status]}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ marginLeft: '16px', marginRight: '16px' }}>
        <Grid container spacing={2} sx={sectionDetailsStyle}>
          <Grid item xs={3}>
            <Typography variant="body1" fontWeight="500" mb={0.5}>
              Details
            </Typography>
            <TypographyDash variant="body2" value={project?.description} />
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1" fontWeight="500" mb={0.5}>
              Special Instructions
            </Typography>
            <TypographyDash
              variant="body2"
              value={project?.specialInstructions}
            />
          </Grid>
          <Grid item xs={5}>
            <Typography variant="body1" fontWeight="500" mb={0.5}>
              Project Attachments
            </Typography>
            <Box>
              {project.attachments.map((attachment) => (
                <AttachmentBox
                  key={attachment.id}
                  onClickFile={() => downloadAttachment(attachment.uuid)}
                  fileName={attachment.fileName}
                />
              ))}
              {!project?.attachments.length && (
                <AttachmentBox fileName="No Attachments" />
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={sectionDetailsStyle}>
          <Grid item xs={6}>
            <Box>
              <Button
                onClick={() => {
                  setModalOpen(true);
                }}
                variant="outlined"
                size="medium"
              >
                Send a message
              </Button>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}
          >
            <Box>
              <Button
                variant="outlined"
                size="medium"
                onClick={(e) => onDeclineProject(e, project)}
              >
                Decline
              </Button>
            </Box>
            <Box>
              <Button
                size="medium"
                onClick={(e) => onAcceptProject(e, project)}
              >
                Accept
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
      <MessageModal
        open={isModalOpen}
        projectId={project.id}
        setOpen={setModalOpen}
        manager={project.projectOwner}
      />
    </Accordion>
  );
};

export default ProjectCard;
